import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from "@angular/common";

import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';

import { db } from '@app/offline-tools/dexie-db/app-storage-db';

import { HistoryMenuItem } from '@app/models';
import { SharedUtilsService } from './shared-utils.service';
import { SsModuleService } from './ss-module.service';

@Injectable({ providedIn: 'root' })
export class HistoryMenuService implements OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  private _destroyed$ = new Subject<any>();
  private historyMenuToggledObs$ = new Subject<any>();

  public currentMenu;
  public lastModuleAndRecord;

  menuStorageKey: string = 'history-menu';
  allModules;

  constructor(
    private _sharedService: SharedUtilsService,
    private _moduleService: SsModuleService,
    private router: Router,
    private location: Location
  ) {
    this.initHistoryItems();
    // this.listenForSectionChange();
  }

  ngOnDestroy() {
    this._destroyed$.next(null);
    this._destroyed$.complete();
  }


  async initHistoryItems() {
    await this.migrateLocalToIndexedDB(); //! Remove after all users have had time to log in. Get it out of their storage.
    this.currentMenu = await this.getHistoryMenuItemsFromStorage();
  }


  async setCurrentMenuFromStorage(): Promise<void> {
    if (!db?.historyMenuItems) {
      console.log('Storage history menu table not set up — skipping menu load.');
      this.currentMenu = [];
      return;
    }

    try {
      const menuFromStorage = await db.historyMenuItems.orderBy('visitedAt').reverse().toArray();
      this.currentMenu = menuFromStorage;
    } catch (_err) {
      console.log('Error setting storage history items to local variable: ', _err?.message);
      this.currentMenu = [];
    }
    // console.log('All history items currently in storage: ', currentItems);
  }


  async getHistoryMenuItemsFromStorage(): Promise<HistoryMenuItem[]> {
    await this.setCurrentMenuFromStorage();
    return this.currentMenu;
  }


  async migrateLocalToIndexedDB() {
    // Get the local storage menu items and copy them to dexie if present. Will be cleared once migrated.
    const localMenu = this._sharedService.getFromLocal(this.menuStorageKey) || [];
    if (!localMenu.length || !db?.historyMenuItems) return;

    // Check if IndexedDB is already populated to avoid duplicate migration
    const count = await db.historyMenuItems.count();
    if (count > 0) {
      // console.log('IndexedDB already has data, skipping migration.');
      return;
    }

    try {
      // Bulk add local items to IndexedDB.
      await db.historyMenuItems.bulkAdd(localMenu);
      // console.log(`Migrated ${localMenu.length} items from localStorage to IndexedDB.`);
    } catch (err) {
      console.log('Failed migrating local menu to IndexedDB:', err);
    }

    this._sharedService.clearKeyFromLocal(this.menuStorageKey); // Clear from storage so it does not add again when clearing dexie items.
  }


  listenForSectionChange() {
    this.router.events.pipe(takeUntil(this.ngUnsubscribe)).subscribe(_routeEvent => {
      this._destroyed$.next(null);
      this._destroyed$.complete();

      if (_routeEvent instanceof NavigationEnd) {
        // Navigation Ended Successfully.
        setTimeout(() => {
          // console.log('NAVIGATION END route event. Loading last page to history.');
          this.loadPageToHistory(_routeEvent);
        }, 1000);
      }
    });
  }


  async removeInvalidItems(_itemState): Promise<void> {
    try {
      // Find the matching item by visitedAt and remove it from storage.
      const match = await db?.historyMenuItems?.where('visitedAt')?.equals(_itemState?.visitedAt)?.first() ?? null;
      if (match?.id != null) await db.historyMenuItems.delete(match.id);
    } catch (_err) {
      console.log('Failed to remove invalid item from IndexedDB:', _err?.message);
    }

    // Now refresh local cache from IndexedDB to make sure it stays in sync.
    await this.setCurrentMenuFromStorage();
  }


  async addToMenu(_item: any): Promise<void> {
    const lastRecordInfo = _item;
    if (!lastRecordInfo) return;

    if (!db?.historyMenuItems) {
      console.log('Storage history menu table not setup up yet.');
      return;
    }

    try {
      // Load current menu from IndexedDB
      const currentMenu = await db.historyMenuItems.orderBy('visitedAt').reverse().toArray();

      // If this route already exists at the top, do nothing.
      if (currentMenu[0]?.route === lastRecordInfo?.url) return;

      const routeAction: HistoryMenuItem = {
        id: this._sharedService.createObjectId(),
        label: lastRecordInfo?.module?.label || '',
        moduleName: lastRecordInfo?.module?.name,
        route: lastRecordInfo?.url,
        lastModuleAndRecord: lastRecordInfo,
        visitedAt: new Date()
      };

      await db.historyMenuItems.add(routeAction); // Add new item.

      // Limit to 50 records.
      const count = await db.historyMenuItems.count();
      if (count > 50) {
        const itemsToDelete = await db.historyMenuItems.orderBy('visitedAt').limit(count - 50).toArray();
        const idsToDelete = itemsToDelete.map(item => item.id);
        await db.historyMenuItems.bulkDelete(idsToDelete);
      }

      await this.setCurrentMenuFromStorage(); // Refresh local variable from storage.
    } catch (_err) {
      console.log('Error in adding item to storage:', _err?.message || _err);
    }
  }


  async loadPageToHistory(_routeEvent: NavigationEnd): Promise<void> {
    this.allModules = this._moduleService.allModules;

    const routePath = this.location.path();
    const routeModuleInfo = this.allModules?.find(_am => routePath.includes(_am?.name));
    const lastRecordInfo = this.lastModuleAndRecord;
    // console.log('Last Record Info: ', lastRecordInfo);

    if (lastRecordInfo && db?.historyMenuItems) {
      try {
        const latest = await db.historyMenuItems.orderBy('visitedAt').reverse().first();

        if (!latest || latest?.route !== lastRecordInfo?.url) {
          const routeAction: HistoryMenuItem = {
            id: this._sharedService.createObjectId(),
            label: lastRecordInfo?.module?.label || routePath,
            moduleName: lastRecordInfo?.module?.name,
            route: routePath,
            lastModuleAndRecord: lastRecordInfo,
            visitedAt: new Date()
          };

          await db.historyMenuItems.add(routeAction); // Add last action.

          // Limit to 50 records.
          const count = await db.historyMenuItems.count();
          if (count > 50) {
            const itemsToDelete = await db.historyMenuItems.orderBy('visitedAt').limit(count - 50).toArray();
            const idsToDelete = itemsToDelete.map(item => item.id);
            await db.historyMenuItems.bulkDelete(idsToDelete);
          }

          await this.setCurrentMenuFromStorage(); // Refresh local variable.
        }
      } catch (err) {
        console.log('Error in loadPageToHistory():', err?.message || err);
      }
    }

    this.triggerMenuToggle();
  }


  listenForHistoryMenuToggle() {
    return this.historyMenuToggledObs$;
  }

  triggerMenuToggle() {
    this.historyMenuToggledObs$.next(this.currentMenu);
  }


  async recheckMenu(): Promise<void> {
    await this.setCurrentMenuFromStorage();
  }


  async resetMenu(): Promise<void> {
    try {
      if (db?.historyMenuItems) await db.historyMenuItems.clear(); // Clear IndexedDB table.
      else console.log('Storage history menu table not set up - skipping DB clear.');

      // Clear local variables.
      this.currentMenu = [];
      this.lastModuleAndRecord = null;
    } catch (_err) {
      console.log('Failed to reset history items in IndexedDB:', _err?.message);
    }
  }


  // async showMenu() {
  //   await this.recheckMenu();

  //   const historyMenuRef = this._bottomSheet.open(HistoryMenuComponent, {backdropClass: 'history-menu-backdrop', panelClass: 'history-menu-panel'});
  //   const instance = historyMenuRef.instance;

  //   instance.currentMenuItems = this.currentMenu;

  //   historyMenuRef.afterDismissed().pipe(takeUntil(this._destroyed$)).subscribe(async _menu => {
  //     const menuAfterClosing = instance.currentMenuItems;

  //     // check if user cleared menu.
  //     if (menuAfterClosing && !menuAfterClosing.length) {
  //       // User cleared menu
  //       await this.resetMenu();
  //     }
  //   });
  // }
}